/* eslint-disable react-hooks/rules-of-hooks */

import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ignoredFilters } from '../components/NewFilterSummary/constants';
import { SearchParamEnum } from '../constants';

interface IFilterParams<T> {
  filters?: T;
}

export default function newUseFilterParams<T = Record<string, string>>({
  filters,
}: IFilterParams<T>) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const changeFilter = useCallback(
    (name: keyof T, value: string) => {
      const current = new URLSearchParams(Array.from(searchParams.entries()));

      if (!value) {
        current.delete(name as string);
      } else {
        current.set(name as string, value);
      }

      current.delete(SearchParamEnum.PAGE);

      const search = current.toString();
      const query = `?${search}`;

      navigate(query);
    },
    [navigate, searchParams],
  );

  const changeMultiple = useCallback(
    (params: { name: keyof T; value: string }[]) => {
      const current = new URLSearchParams(searchParams);

      params.forEach(({ name, value }) => {
        if (value) {
          current.set(name as string, value);
        } else {
          current.delete(name as string);
        }
      });

      current.delete(SearchParamEnum.PAGE);

      const query = `?${current.toString()}`;
      navigate(query);
    },
    [navigate, searchParams],
  );

  const clearFilterName = useCallback(
    (name: keyof T) => {
      const current = new URLSearchParams(searchParams.toString());

      current.delete(name as string);

      const search = current.toString();
      const query = search ? `?${search}` : '';

      navigate(query);
    },
    [navigate, searchParams],
  );

  const clearMultipleFilterNames = useCallback(
    (names: (keyof T)[]) => {
      const current = new URLSearchParams(Array.from(searchParams.entries()));

      names.forEach(name => {
        current.delete(name as string);
      });

      const search = current.toString();
      const query = `?${search}`;

      navigate(query);
    },
    [navigate, searchParams],
  );

  const clearAll = () => {
    navigate('');
  };

  const prepareFilter = useCallback(() => {
    if (!filters) return {} as T;

    const filterParams: Record<string, unknown> = {};

    Object.keys(filters).forEach(el => {
      const f: Record<string, string> = filters;

      const defaultValue = f[el];

      filterParams[el] = searchParams.get(el) ?? defaultValue;
    });

    return filterParams as T;
  }, [filters, searchParams]);

  const params: T = useMemo(() => prepareFilter(), [prepareFilter]);

  const filteredParams = Array.from(searchParams.entries()).filter(
    ([key]) => !ignoredFilters.includes(key),
  );

  const filterCount = filteredParams.length;

  return {
    changeFilter,
    changeMultiple,
    clearAll,
    params,
    clearFilterName,
    clearMultipleFilterNames,
    filterCount,
  };
}
